import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore'
import { db, auth } from '@/js/firebase'
import { logger } from '@/services/loggerService'

/**
 * The generic global-default system prompt for episode title / thumbnail-text /
 * show-notes generation. Brand-specific voice goes in a per-show override
 * (shows/{showId}/episodeContent/template.prompt), mirroring
 * discussionGuidePromptService.
 *
 * This layer covers VOICE only. The hard limits (paragraph, takeaway and
 * timestamp counts, no em dashes) are appended server-side by
 * buildOutputContract() in functions/episodeContentGeneration/prompt.js, so an
 * admin editing this prompt cannot drop them. Keep this copy byte-identical to
 * DEFAULT_EPISODE_CONTENT_PROMPT there.
 */
export const DEFAULT_EPISODE_CONTENT_PROMPT = `You generate a podcast episode's headline (title), on-thumbnail text, and description from a transcript.

Core principle: title, thumbnail, and description work together. The thumbnail creates curiosity. The title clarifies the core idea. The description sells the episode without spoiling it. They reinforce each other without repeating the same wording.

1. Title Rules
Proven structures: (1) Big Idea + Specific Angle: a clear concept with a distinct perspective; (2) Tension or Reframe: challenge a common belief; (3) Outcome or Strategy: what this episode unlocks.
A strong title is clear before clever, contains stakes or tension, and stays broadly relevant beyond the guest's name. Avoid generic phrasing, long sentences, clickbait, and empty inspiration. Concise but specific.

2. Thumbnail Text Rules
Must NOT repeat the title. 2 to 6 words, bold and readable on mobile, focused on tension, scale, or vision. It should spark interest in seconds.

3. Episode Overview
Write a trailer, not a recap. Frame the stakes or the question the episode answers, establish who the guest is and why their perspective matters, and close on the larger question the conversation leaves open. Name the terrain the conversation covers, never the conclusions it reaches. Do not quote figures, dollar amounts, statistics, or specific claims: those belong in the episode, and a listener who already has the answer has no reason to press play. Keep it tight; nobody reads a long description.

4. Key Takeaways
Curiosity hooks, not summaries. Each one names what a listener will get without handing it over. Prefer "Why access to opportunity can matter as much as the opportunity itself" over "Plummer explains that access matters because of procurement rules." Openers like Why, What, How, and Where work well.

5. Timestamps
Chronological, starting at the intro. Each label is a short topic phrase, not a sentence, and marks a real turning point in the conversation.

Write plainly. No hype, no jargon, no empty inspiration.`

class EpisodeContentPromptService {
  constructor() {
    // Admin-editable global default (falls back to the hardcoded constant).
    this.globalRef = doc(db, 'settings', 'episodeContentPrompt')
  }

  /**
   * The saved global prompt override, or '' if none (editor shows the built-in
   * DEFAULT as placeholder in that case).
   */
  async getGlobalPromptRaw() {
    try {
      const snap = await getDoc(this.globalRef)
      return snap.exists() ? (snap.data().prompt || '') : ''
    } catch (error) {
      logger.error('[EpisodeContentPrompt] Failed to read global prompt', { error: error.message })
      return ''
    }
  }

  /** The effective global prompt: saved settings doc, else the built-in default. */
  async getGlobalPrompt() {
    const saved = (await this.getGlobalPromptRaw()).trim()
    return saved || DEFAULT_EPISODE_CONTENT_PROMPT
  }

  async saveGlobalPrompt(prompt) {
    const user = auth.currentUser
    if (!user) throw new Error('User not authenticated')
    await setDoc(this.globalRef, {
      prompt,
      updatedAt: serverTimestamp(),
      updatedBy: user.uid,
    }, { merge: true })
    logger.info('[EpisodeContentPrompt] Global prompt saved')
  }

  /**
   * Per-show prompt override (brand voice). Returns null when the show uses the
   * global default.
   */
  async getShowTemplate(showId) {
    if (!showId) return null
    try {
      const snap = await getDoc(doc(db, 'shows', showId, 'episodeContent', 'template'))
      return snap.exists() ? snap.data() : null
    } catch (error) {
      logger.error('[EpisodeContentPrompt] Failed to get show template', { error: error.message, showId })
      return null
    }
  }

  async saveShowTemplate(showId, prompt) {
    const user = auth.currentUser
    if (!user) throw new Error('User not authenticated')
    await setDoc(doc(db, 'shows', showId, 'episodeContent', 'template'), {
      prompt,
      updatedAt: serverTimestamp(),
      updatedBy: user.uid,
    }, { merge: true })
    logger.info('[EpisodeContentPrompt] Show template saved', { showId })
  }

  async removeShowTemplate(showId) {
    await this.saveShowTemplate(showId, '')
  }

  /**
   * Resolve the effective prompt for a show: per-show override → admin global
   * default (settings doc) → built-in default constant.
   */
  async resolvePrompt(showId) {
    const tmpl = await this.getShowTemplate(showId)
    const override = tmpl?.prompt?.trim()
    if (override) return override
    return await this.getGlobalPrompt()
  }
}

export const episodeContentPromptService = new EpisodeContentPromptService()
